import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Monitor, TestTube2 } from "lucide-react";
import { useMode, SCREENS_WITH_MODE_TOGGLE } from "@/providers/ModeProvider";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// Toggle this to show/hide the global mode toggle
const SHOW_MODE_TOGGLE = false;

export function GlobalModeToggle() {
  const { areAllScreensLive, setAllScreensToLive, setAllScreensToDemo, screenModes } = useMode();
  
  // Hide the toggle if SHOW_MODE_TOGGLE is false
  if (!SHOW_MODE_TOGGLE) {
    return null;
  }

  const handleToggle = (checked: boolean) => {
    if (checked) {
      setAllScreensToLive();
    } else {
      setAllScreensToDemo();
    }
  };

  // Count how many screens are in each mode
  const liveCount = SCREENS_WITH_MODE_TOGGLE.filter((screenId) => screenModes[screenId] === "live").length;
  const totalCount = SCREENS_WITH_MODE_TOGGLE.length;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-2">
            <TestTube2 className={`h-4 w-4 ${!areAllScreensLive ? "text-yellow-600" : "text-muted-foreground"}`} />
            <Switch
              checked={areAllScreensLive}
              onCheckedChange={handleToggle}
              aria-label="Toggle all screens to live mode"
            />
            <Monitor className={`h-4 w-4 ${areAllScreensLive ? "text-green-600" : "text-muted-foreground"}`} />
            {!areAllScreensLive && (
              <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200 text-xs">
                {liveCount}/{totalCount} Live
              </Badge>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <div className="space-y-1">
            <p className="font-medium">
              {areAllScreensLive ? "All screens in Live mode" : "Some screens in Demo mode"}
            </p>
            <p className="text-xs text-muted-foreground">
              {areAllScreensLive
                ? "All screens are connected to live data. Toggle off to switch all screens to Demo mode."
                : "Toggle on to switch all screens to Live mode."}
            </p>
            <div className="pt-1 border-t mt-1">
              <p className="text-xs font-medium mb-1">Screen Status:</p>
              <ul className="text-xs space-y-0.5">
                {SCREENS_WITH_MODE_TOGGLE.map((screenId) => (
                  <li key={screenId} className="flex items-center gap-1">
                    <span
                      className={`w-2 h-2 rounded-full ${
                        screenModes[screenId] === "live" ? "bg-green-500" : "bg-yellow-500"
                      }`}
                    />
                    <span className="capitalize">{screenId.replace(/-/g, " ")}</span>
                    <span className="text-muted-foreground">({screenModes[screenId]})</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
